import React, { useState, useEffect } from 'react';
import { addDays, startOfDay } from 'date-fns';
import { authService } from '../auth/authService';
import { graphService } from '../graph/graphService';
import { Person, TimeSlot } from '../graph/types';
import { findCommonTimeSlots, formatTimeSlotsForEmail, getTimezoneAbbreviation } from '../logic/availabilityLogic';
import { PeoplePicker } from '../components/PeoplePicker';
import { DateRangeSelector } from '../components/DateRangeSelector';
import { ResultsList } from '../components/ResultsList';
import { SavedGroups } from '../components/SavedGroups';
import { Settings } from '../components/Settings';
import { getSettings, saveSettings } from '../utils/groupStorage';
import './TaskPane.css';

// Get the user's timezone from the browser
const getBrowserTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'America/New_York'; // Fallback
  }
};

export const TaskPane: React.FC = () => {
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  // View state
  const [showSettings, setShowSettings] = useState(false);

  // Auto-detect user's timezone from browser
  const userTimeZone = getBrowserTimeZone();

  // User selections
  const [selectedPeople, setSelectedPeople] = useState<Person[]>([]);
  const [startDate, setStartDate] = useState<Date>(startOfDay(new Date()));
  const [endDate, setEndDate] = useState<Date>(startOfDay(addDays(new Date(), 7)));
  const [workingHoursStart, setWorkingHoursStart] = useState('09:00');
  const [workingHoursEnd, setWorkingHoursEnd] = useState('17:00');
  // Recipient timezone defaults to user's timezone, can be changed for display
  const [recipientTimeZone, setRecipientTimeZone] = useState(userTimeZone);
  const [includeAttendees, setIncludeAttendees] = useState(true);

  // Results state
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  // Initialize Office.js and auth
  useEffect(() => {
    Office.onReady(() => {
      console.log('Office.js is ready');
      initializeAuth();
    });
  }, []);

  const initializeAuth = async () => {
    try {
      await authService.initialize();
      setIsAuthenticated(authService.isAuthenticated());

      // Load settings (from roaming settings or localStorage)
      const settings = getSettings();
      setIncludeAttendees(settings.includeAttendeesInEmail);
      setWorkingHoursStart(settings.workingHoursStart);
      setWorkingHoursEnd(settings.workingHoursEnd);
    } catch (error) {
      console.error('Auth initialization failed:', error);
    }
  };

  const handleLogin = async () => {
    setIsAuthenticating(true);
    setAuthError(null);

    try {
      await authService.login();
      setIsAuthenticated(true);
    } catch (error) {
      console.error('Login failed:', error);
      setAuthError(error instanceof Error ? error.message : 'Login failed');
    } finally {
      setIsAuthenticating(false);
    }
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
      setIsAuthenticated(false);
      setSelectedPeople([]);
      setAvailableSlots([]);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleIncludeAttendeesChange = async (value: boolean) => {
    setIncludeAttendees(value);
    try {
      await saveSettings({
        includeAttendeesInEmail: value,
        workingHoursStart,
        workingHoursEnd,
      });
    } catch (error) {
      console.error('Failed to save settings:', error);
    }
  };

  const handleWorkingHoursStartChange = async (value: string) => {
    setWorkingHoursStart(value);
    try {
      await saveSettings({
        includeAttendeesInEmail: includeAttendees,
        workingHoursStart: value,
        workingHoursEnd,
      });
    } catch (error) {
      console.error('Failed to save settings:', error);
    }
  };

  const handleWorkingHoursEndChange = async (value: string) => {
    setWorkingHoursEnd(value);
    try {
      await saveSettings({
        includeAttendeesInEmail: includeAttendees,
        workingHoursStart,
        workingHoursEnd: value,
      });
    } catch (error) {
      console.error('Failed to save settings:', error);
    }
  };

  const handleFindCommonTimes = async () => {
    if (selectedPeople.length === 0) {
      setSearchError('Please select at least one attendee');
      return;
    }

    setIsSearching(true);
    setSearchError(null);
    setAvailableSlots([]);

    try {
      // Get schedules for all selected people
      const emailAddresses = selectedPeople.map((p) => p.emailAddress);

      // Extend end date to end of day (23:59:59) to cover full range
      const endDateTime = new Date(endDate);
      endDateTime.setHours(23, 59, 59, 999);

      const schedules = await graphService.getSchedule(
        emailAddresses,
        startDate,
        endDateTime,
        userTimeZone, // Use auto-detected browser timezone
        15 // interval in minutes (15 min for granular availability)
      );


      // Find available time blocks
      // Use 15-minute intervals for granularity, merged into continuous blocks
      const slots = findCommonTimeSlots({
        schedules,
        startDate,
        endDate,
        durationMinutes: 15, // 15-minute minimum slots, merged into blocks
        intervalMinutes: 15,
        workingHoursStart,
        workingHoursEnd,
        timeZone: userTimeZone, // Use auto-detected browser timezone
        maxResults: 20, // Limit number of time blocks shown
      });

      setAvailableSlots(slots);

      if (slots.length === 0) {
        setSearchError(
          'No common available times found. Try expanding your date range or adjusting working hours.'
        );
      }
    } catch (error) {
      console.error('Error finding common times:', error);
      setSearchError(
        error instanceof Error
          ? error.message
          : 'Failed to find common times. Please check your permissions and try again.'
      );
    } finally {
      setIsSearching(false);
    }
  };

  const handleInsertIntoEmail = async (slots: TimeSlot[]) => {
    if (slots.length === 0) return;

    const html = formatTimeSlotsForEmail(
      slots,
      recipientTimeZone,
      getTimezoneAbbreviation(recipientTimeZone),
      includeAttendees ? selectedPeople : undefined,
      true // Use HTML format for email insertion
    );

    try {
      // Use Office.js to insert HTML at cursor position
      Office.context.mailbox.item!.body.setSelectedDataAsync(
        html,
        { coercionType: Office.CoercionType.Html },
        (result) => {
          if (result.status === Office.AsyncResultStatus.Failed) {
            console.error('Failed to insert text:', result.error);
            alert('Failed to insert text into email. Please try copying instead.');
          } else {
            console.log('HTML inserted successfully');
            // Optionally show success message
          }
        }
      );
    } catch (error) {
      console.error('Error inserting into email:', error);
      alert('Failed to insert into email. Please use the Copy button instead.');
    }
  };

  // Not authenticated view
  if (!isAuthenticated) {
    return (
      <div className="taskpane">
        <div className="header">
          <h1 className="title">Find Time</h1>
        </div>

        <div className="auth-container">
          <p className="auth-description">
            Sign in to find available meeting times for your coworkers and insert them into
            your email.
          </p>

          <button
            className="primary-btn"
            onClick={handleLogin}
            disabled={isAuthenticating}
          >
            {isAuthenticating ? 'Signing in...' : 'Sign In with Microsoft'}
          </button>

          {authError && <div className="error-message">{authError}</div>}

          <div className="permissions-note">
            <strong>Required permissions:</strong>
            <ul>
              <li>Read basic calendar free/busy information</li>
              <li>Search company directory</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // Authenticated view
  return (
    <div className="taskpane">
      <div className="header">
        <h1 className="title">Find Time</h1>
        <button className="logout-btn" onClick={handleLogout}>
          Sign Out
        </button>
      </div>

      <div className="content">
        {showSettings ? (
          <Settings
            workingHoursStart={workingHoursStart}
            workingHoursEnd={workingHoursEnd}
            detectedTimeZone={userTimeZone}
            includeAttendees={includeAttendees}
            onWorkingHoursStartChange={handleWorkingHoursStartChange}
            onWorkingHoursEndChange={handleWorkingHoursEndChange}
            onIncludeAttendeesChange={handleIncludeAttendeesChange}
            onClose={() => setShowSettings(false)}
          />
        ) : (
          <>
            {/* Saved Groups */}
            <SavedGroups
              currentPeople={selectedPeople}
              onLoadGroup={setSelectedPeople}
            />

            {/* Section 1: Attendees */}
            <PeoplePicker
              selectedPeople={selectedPeople}
              onPeopleChange={setSelectedPeople}
            />

            {/* Section 2: Date & Time Constraints */}
            <DateRangeSelector
              startDate={startDate}
              endDate={endDate}
              onStartDateChange={setStartDate}
              onEndDateChange={setEndDate}
            />

            {/* Display Time Zone (for recipient) */}
            <div className="section">
              <label className="section-label">Display Available Times In</label>
              <select
                className="select-input"
                value={recipientTimeZone}
                onChange={(e) => setRecipientTimeZone(e.target.value)}
              >
                <option value={userTimeZone}>My Time Zone ({userTimeZone.split('/').pop()?.replace('_', ' ')})</option>
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
                <option value="Europe/London">London (GMT)</option>
                <option value="Europe/Paris">Paris (CET)</option>
                <option value="Asia/Tokyo">Tokyo (JST)</option>
                <option value="Australia/Sydney">Sydney (AEDT)</option>
                <option value="UTC">UTC</option>
              </select>
              <span className="field-hint">Choose the recipient's timezone to display times in their local time</span>
            </div>

            {/* Find Times Button */}
            <div className="find-section">
              <button
                className="primary-btn find-btn"
                onClick={handleFindCommonTimes}
                disabled={isSearching || selectedPeople.length === 0}
              >
                {isSearching ? 'Searching...' : 'Find Available Times'}
              </button>
            </div>

            {/* Error Message */}
            {searchError && <div className="error-message">{searchError}</div>}

            {/* Section 3: Results */}
            {availableSlots.length > 0 && (
              <ResultsList
                slots={availableSlots}
                timeZone={recipientTimeZone}
                attendees={selectedPeople}
                includeAttendees={includeAttendees}
                onInsertIntoEmail={handleInsertIntoEmail}
              />
            )}

            {/* Settings Button */}
            <div className="settings-button-container">
              <button className="settings-btn" onClick={() => setShowSettings(true)}>
                Settings
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
